import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, X, Send, ArrowUpRight } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { chatStrings } from "@/i18n/chat";
import { API } from "@/lib/api";

interface Msg {
  role: "user" | "assistant";
  text: string;
}

const getSessionId = () => {
  let id = localStorage.getItem("r3-chat-session");
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem("r3-chat-session", id);
  }
  return id;
};

export const ChatWidget = () => {
  const { lang, setLang } = useLanguage();
  const c = chatStrings[lang];
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const sessionRef = useRef<string>("");

  useEffect(() => {
    sessionRef.current = getSessionId();
  }, []);

  useEffect(() => {
    setMsgs((prev) =>
      prev.length <= 1 ? [{ role: "assistant", text: c.greeting }] : prev,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [msgs, open]);

  const pushAssistantDelta = (delta: string) =>
    setMsgs((m) => {
      const copy = [...m];
      const last = copy[copy.length - 1];
      copy[copy.length - 1] = { role: "assistant", text: last.text + delta };
      return copy;
    });

  const setLastAssistant = (text: string) =>
    setMsgs((m) => {
      const copy = [...m];
      copy[copy.length - 1] = { role: "assistant", text };
      return copy;
    });

  const send = async (rawText: string) => {
    const text = rawText.trim();
    if (!text || busy) return;
    setMsgs((m) => [...m, { role: "user", text }]);
    setInput("");
    setBusy(true);
    setMsgs((m) => [...m, { role: "assistant", text: "" }]);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: sessionRef.current,
          message: text,
          language: lang,
        }),
      });
      if (!res.ok || !res.body) throw new Error(`status ${res.status}`);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split("\n");
        buf = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data:")) continue;
          try {
            const payload = JSON.parse(line.slice(5).trim());
            if (payload.delta) pushAssistantDelta(payload.delta);
            if (payload.error) throw new Error("stream error");
          } catch (e) {
            if ((e as Error).message === "stream error") throw e;
          }
        }
      }
    } catch {
      setLastAssistant(c.error);
    }
    setBusy(false);
  };

  const onQuickReply = (id: string, message: string) => {
    if (id === "spanish") {
      setLang("es");
      setMsgs((m) => [
        ...m,
        { role: "user", text: "Hablar Español" },
        { role: "assistant", text: chatStrings.es.spanishSwitched },
      ]);
      return;
    }
    if (id === "bid") navigate("/request-a-bid");
    send(message);
  };

  return (
    <>
      <AnimatePresence>
        {!open && (
          <motion.button
            data-testid="chat-open-btn"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 16 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            onClick={() => setOpen(true)}
            className="group fixed bottom-[76px] right-5 z-[70] flex items-center gap-2.5 rounded-full bg-gold py-3 pl-4 pr-5 shadow-lg shadow-black/40 transition-colors duration-300 hover:bg-golddark md:bottom-7 md:right-7"
            aria-label={c.openLabel}
          >
            <MessageCircle className="h-5 w-5 text-ink" />
            <span className="text-xs font-black uppercase tracking-[0.12em] text-ink">
              {c.openLabel}
            </span>
          </motion.button>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-panel"
            initial={{ opacity: 0, y: 32, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 32, scale: 0.98 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-x-3 bottom-[76px] z-[80] flex h-[min(600px,72dvh)] flex-col overflow-hidden border border-white/10 bg-charcoal shadow-2xl shadow-black/60 sm:inset-x-auto sm:bottom-6 sm:right-6 sm:w-[380px]"
          >
            <div className="flex items-center justify-between border-b border-white/10 bg-ink px-5 py-4">
              <div className="flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-gold font-display text-sm font-black text-ink">
                  R3
                </span>
                <div>
                  <p className="font-display text-base font-black tracking-tight text-white">
                    {c.title}
                  </p>
                  <p className="text-[11px] text-neutral-500">{c.subtitle}</p>
                </div>
              </div>
              <button
                data-testid="chat-close-btn"
                onClick={() => setOpen(false)}
                className="flex h-8 w-8 items-center justify-center rounded-full border border-white/15 text-neutral-400 transition-colors duration-300 hover:border-gold hover:text-gold"
                aria-label={c.close}
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div
              ref={scrollRef}
              data-testid="chat-messages"
              className="flex-1 space-y-4 overflow-y-auto px-4 py-5"
            >
              {msgs.map((m, i) => (
                <div
                  key={i}
                  className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    data-testid={`chat-message-${i}`}
                    className={`max-w-[85%] whitespace-pre-wrap px-4 py-3 text-sm leading-relaxed ${
                      m.role === "user"
                        ? "rounded-2xl rounded-br-sm bg-gold font-semibold text-ink"
                        : "rounded-2xl rounded-bl-sm border border-white/10 bg-ink text-neutral-200"
                    }`}
                  >
                    {m.text ||
                      (busy && i === msgs.length - 1 ? (
                        <span className="flex gap-1 py-1">
                          {[0, 1, 2].map((d) => (
                            <motion.span
                              key={d}
                              animate={{ opacity: [0.25, 1, 0.25] }}
                              transition={{
                                duration: 1.1,
                                repeat: Infinity,
                                delay: d * 0.18,
                              }}
                              className="inline-block h-1.5 w-1.5 rounded-full bg-gold"
                            />
                          ))}
                        </span>
                      ) : (
                        ""
                      ))}
                  </div>
                </div>
              ))}
            </div>

            {msgs.length <= 1 && !busy && (
              <div
                data-testid="chat-quick-replies"
                className="flex flex-wrap gap-2 border-t border-white/10 px-4 py-3"
              >
                {c.quickReplies.map((q) => (
                  <button
                    key={q.id}
                    data-testid={`chat-quick-${q.id}`}
                    onClick={() => onQuickReply(q.id, q.message)}
                    className="flex items-center gap-1 rounded-full border border-white/15 px-3.5 py-2 text-[11px] font-bold uppercase tracking-[0.08em] text-neutral-300 transition-colors duration-300 hover:border-gold hover:text-gold"
                  >
                    {q.label}
                    {q.id === "bid" && <ArrowUpRight className="h-3 w-3" />}
                  </button>
                ))}
              </div>
            )}

            <form
              data-testid="chat-form"
              onSubmit={(e) => {
                e.preventDefault();
                send(input);
              }}
              className="flex items-center gap-2 border-t border-white/10 bg-ink px-3 py-3"
            >
              <input
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={c.placeholder}
                maxLength={1000}
                className="flex-1 bg-transparent px-3 py-2 text-sm text-white placeholder:text-neutral-600 focus:outline-none"
              />
              <button
                data-testid="chat-send-btn"
                type="submit"
                disabled={busy || !input.trim()}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gold text-ink transition-colors duration-300 hover:bg-golddark disabled:opacity-40"
                aria-label={c.send}
              >
                <Send className="h-4 w-4" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
